<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '@/lib/components/ui/button';
  import HelpTooltip from './HelpTooltip.svelte';
  import type { SlopeIntervals } from '@/Util/api';

  export let open = false;
  // Where the workout is going, e.g. "Zwift" or "ZWO".
  export let destination = '';
  export let action: 'send' | 'download' = 'send';
  // Called once per prompt: with the chosen option, or null when the dialog is dismissed.
  export let onSettle: (choice: SlopeIntervals | null) => void = () => {};

  const options: { value: SlopeIntervals; title: string; erg: string; summary: string; help: string }[] = [
    {
      value: 'free_ride',
      title: 'Slopes as free ride',
      erg: 'ERG on',
      summary: 'Slope intervals have no power target. Ride the rest of the workout in ERG mode.',
      help: 'Each slope interval becomes a free ride interval with no power target, so you can keep ERG mode on for the rest of the workout and ride the free ride intervals at whatever effort you choose. Formats that can only hold power targets, such as ERG files, use 0 W, which leaves your trainer at its minimum resistance.',
    },
    {
      value: 'power',
      title: 'Slopes as power targets',
      erg: 'ERG off',
      summary: 'Every interval has a power target. Ride the whole workout with ERG mode off.',
      help: "Each slope interval uses its target power, so every interval has a power target. Ride with ERG mode off and shift gears to hold each target, as you would on a real climb. With ERG mode on, your trainer would set the resistance for you and the slope intervals wouldn't ride the way they were designed.",
    },
  ];
</script>

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target" onOpenChange={(isOpen) => { if (!isOpen) onSettle(null); }}>
  <Dialog.Content class="max-w-md">
    <Dialog.Header>
      <Dialog.Title>{action === 'send' ? `Send to ${destination}` : `Download ${destination} file`}</Dialog.Title>
      <Dialog.Description>
        This is a mixed mode workout. Some intervals set your trainer into slope mode, requiring you to change gears.
        {action === 'send' ? `${destination} workouts` : `${destination} files`} don't support this interval type, so choose how to send the slope intervals.
      </Dialog.Description>
    </Dialog.Header>

    <div class="flex flex-col gap-2">
      {#each options as option}
        <div class="flex items-center gap-1">
          <Button
            variant="outline"
            class="h-auto flex-1 flex-col items-start gap-1 whitespace-normal py-2 text-left"
            on:click={() => onSettle(option.value)}
          >
            <span class="flex w-full items-center justify-between gap-2">
              <span class="font-medium">{option.title}</span>
              <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{option.erg}</span>
            </span>
            <span class="text-xs font-normal text-muted-foreground">{option.summary}</span>
          </Button>
          <HelpTooltip message={`<strong class="text-black">${option.title} (${option.erg})</strong><br/>${option.help}`} />
        </div>
      {/each}
    </div>

    <Dialog.Footer>
      <Button variant="ghost" on:click={() => onSettle(null)}>Cancel</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
